import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Loader from "../Loader/Loader";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";

const formatPoints = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0.00";
  return num.toFixed(2);
};

const EditRewardPointsModal = ({ show, handleClose, user }) => {
  const [existingPoints, setExistingPoints] = useState(null);
  const [newPoints, setNewPoints] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchingWallet, setFetchingWallet] = useState(false);
  const { AsyncPatchAPICall, AsyncGetApiCall } = useApi();

  const loadRewardWallet = async () => {
    if (!user?._id) return;
    setFetchingWallet(true);
    try {
      const result = await AsyncGetApiCall("/flytoken/get-reward-wallet", {
        userId: user._id,
        currency: "EUR",
      });

      if (result?.errors) {
        const fallback = user.rewardPoints ?? 0;
        setExistingPoints(fallback);
        setNewPoints(String(fallback));
      } else if (result?.success) {
        const points = result?.data?.currentPoints ?? user.rewardPoints ?? 0;
        setExistingPoints(points);
        setNewPoints(String(points));
      }
    } catch {
      const fallback = user.rewardPoints ?? 0;
      setExistingPoints(fallback);
      setNewPoints(String(fallback));
    } finally {
      setFetchingWallet(false);
    }
  };

  useEffect(() => {
    if (show && user) {
      setErrors({});
      setExistingPoints(null);
      setNewPoints("");
      loadRewardWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user?._id]);

  const validate = () => {
    const newErrors = {};
    const value = Number(newPoints);
    if (newPoints === "" || Number.isNaN(value)) {
      newErrors.newPoints = "New reward points are required";
    } else if (value < 0) {
      newErrors.newPoints = "Reward points cannot be negative";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !user?._id) return;
    setLoading(true);

    const result = await AsyncPatchAPICall("/flytoken/admin/reward-points", {
      userId: user._id,
      currentPoints: Number(newPoints),
    });

    if (result?.errors) {
      toast.error(result.errors[0]);
    } else if (result?.success) {
      toast.success(result.message || "Reward points updated successfully.");
      handleClose(true);
    }

    setLoading(false);
  };

  const displayName =
    user?.firstName || user?.lastName
      ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
      : user?.email || "User";

  return (
    <>
      {(loading || fetchingWallet) && <Loader />}
      <Modal show={show} onHide={() => handleClose(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit reward points</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-secondary-light mb-16">
            User: <strong>{displayName}</strong>
            {user?.email && (
              <span className="d-block">{user.email}</span>
            )}
          </p>

          <Form.Group className="mb-20">
            <Form.Label className="text-secondary-light">
              Current reward points
            </Form.Label>
            <div
              className="form-control radius-8 bg-neutral-100 text-secondary-light"
              style={{ cursor: "default" }}
            >
              {fetchingWallet
                ? "Loading…"
                : formatPoints(existingPoints ?? user?.rewardPoints ?? 0)}
            </div>
            <Form.Text className="text-secondary-light">
              This is the user&apos;s balance right now (read-only).
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label>New reward points</Form.Label>
            <Form.Control
              type="number"
              name="newPoints"
              min={0}
              step="0.01"
              value={newPoints}
              onChange={(e) => {
                setNewPoints(e.target.value);
                setErrors({});
              }}
              disabled={fetchingWallet}
              isInvalid={!!errors.newPoints}
              placeholder="Enter updated reward points"
            />
            <Form.Control.Feedback type="invalid">
              {errors.newPoints}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || fetchingWallet}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditRewardPointsModal;
